import React, { useLayoutEffect, useMemo, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./MonadLabSection.css";

gsap.registerPlugin(ScrollTrigger);

const AtomEVM: React.FC = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const radarRef = useRef<HTMLDivElement | null>(null);
  const heroBarRef = useRef<HTMLDivElement | null>(null);

  // --- Datos de barras ---
  // Mas barras para que se vean mas delgadas (estilo grafico de lineas)
  const BASE_TOTAL_BARS = 180;
  const isClient = typeof window !== "undefined";
  const isMobileViewport = isClient && window.innerWidth <= 768;
  const TOTAL_BARS = isMobileViewport ? 70 : BASE_TOTAL_BARS;
  const HERO_BAR_INDEX = Math.floor(TOTAL_BARS * 0.72);

  // Ajustes finos para la alineacion radar barra hero (en pixeles)
  // Si quieres mover manualmente el radar respecto a la barra hero,
  // modifica estos valores: positivo = derecha/abajo, negativo = izquierda/arriba.
  const RADAR_OFFSET_X = 0;
  const RADAR_OFFSET_Y = -428;

  const bars = useMemo(() => {
  return Array.from({ length: TOTAL_BARS }, (_, i) => {
    const progress = i / (TOTAL_BARS - 15); // 0 = izquierda, 1 = derecha

    // --- Onda más "imperfecta" ---
    // Combinamos varias frecuencias para tener muchos picos y valles.
    const rawWave =
      Math.sin(i * 0.08) * 10 +          // frecuencia media
      Math.sin(i * 0.035 + 1.3) * 7 +    // frecuencia más larga
      Math.sin(i * 0.19 - 0.2) * 9;      // frecuencia corta (detalles finos)

    // Amortiguación: 1 en la izquierda, casi 0 en la derecha.
    // Así la ola es fuerte al inicio y se "aplana" cerca del radar/hero.
    const damping = 1 - Math.pow(progress, 1.8);
    const wave = rawWave * damping;

    // ----- CONTROL DE INCLINACIÓN DE LA OLA -----
    const leanShift = 0.3;
    const leanStrength = 140;

    // A la izquierda de leanShift -> slope negativo (barras muy bajas)
    // A la derecha de leanShift  -> slope positivo (barras altas)
    const slope = (progress - leanShift) * leanStrength;

    // Altura final
    let height = Math.max(0, 9 + wave + slope + Math.random() * 2.5);

    // La hero mantiene su base; la altura real viene del scaleY del GSAP
    if (i === HERO_BAR_INDEX) height = 13;

    return height;
  });
}, [HERO_BAR_INDEX, TOTAL_BARS]);

  // --- Animación con ScrollTrigger ---
  useLayoutEffect(() => {
    if (
      !containerRef.current ||
      !contentRef.current ||
      !radarRef.current ||
      !heroBarRef.current
    )
      return;

    const ctx = gsap.context(() => {
      const isMobile = window.innerWidth <= 768;
      const radarEl = radarRef.current!;
      const heroEl = heroBarRef.current!;

      const radarRect = radarEl.getBoundingClientRect();
      const heroRect = heroEl.getBoundingClientRect();

      const heroCenterX = heroRect.left + heroRect.width / 2;
      const radarCenterX = radarRect.left + radarRect.width / 2;

      const heroTopY = heroRect.top;
      const radarCenterY = radarRect.top + radarRect.height / 2;

      // Alineamos el centro del radar con la punta de la barra hero
      // y aplicamos los offsets manuales definidos arriba.
      gsap.set(radarEl, {
        x: heroCenterX - radarCenterX + RADAR_OFFSET_X,
        y: heroTopY - radarCenterY + RADAR_OFFSET_Y,
      });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          pin: contentRef.current,
          start: "top top",
          end: "bottom bottom",
          scrub: 1.2,
        },
      });

      // A. Textos
      tl.from(".stat-item-text", {
        y: 20,
        opacity: 0,
        duration: 0.8,
        stagger: 0.1,
        ease: "power2.out",
      });

      // A.1 Contadores num9ricos de stats
      const statValues = gsap.utils.toArray(
        ".monad-lab-stat-value",
      ) as HTMLElement[];

      statValues.forEach((el) => {
        const targetAttr = el.dataset.target;
        if (!targetAttr) return;

        const suffix = el.dataset.suffix ?? "";
        const decimals = Number(el.dataset.decimals ?? "0");
        const target = parseFloat(targetAttr);

        const counter = { value: 0 };

        tl.to(
          counter,
          {
            value: target,
            duration: 1.4,
            ease: "power1.out",
            onUpdate: () => {
              const current = counter.value;
              const formattedNumber =
            decimals > 0
            ? current.toFixed(decimals)
            : Math.round(current).toLocaleString("en-US");

            el.textContent = `${formattedNumber}${suffix}`;
            },
          },
          "<",
        );
      });

      // B. Ola de barras (fondo)
      tl.addLabel("startChart");
      tl.from(
        ".monad-lab-chart-bar.chart-bar",
        {
          scaleY: 0,
          opacity: 0,
          transformOrigin: "bottom",
          duration: 1.5,
          stagger: { amount: 1, from: "start" as const },
          ease: "power1.out",
        },
        "startChart",
      );

      // C. Barra heroica sincronizada con el radar
      tl.fromTo(
        ".monad-lab-chart-bar.hero-bar",
        { scaleY: 0, backgroundColor: "#e5e7eb" },
        {
          scaleY: isMobile ? 10.0 : 9.0,
          backgroundColor: "#ffffffff", // final azul para destacar
          transformOrigin: "bottom",
          duration: 2,
          ease: "power2.inOut",
        },
        "startChart+=0.5",
      );

      // D. Radar (anillos + punto central + glow en s
      tl.fromTo(
        [".radar-ring-static", ".radar-center"],
        { opacity: 0, scale: 0.8 },
        {
          opacity: 1,
          scale: 1,
          duration: 1.5,
          ease: "power2.out",
        },
        "<+=1.2",
      );
    }, containerRef);

    return () => ctx.revert();
  }, [RADAR_OFFSET_X, RADAR_OFFSET_Y]);

  return (
    <section className="monad-lab-section">
      <div ref={containerRef} className="monad-lab-track">
        <div ref={contentRef} className="monad-lab-screen">
          <div className="monad-lab-bg-grid" />

          <div className="monad-lab-top">
            <h2 className="monad-lab-headline stat-item-text">
              Atom unlocks a new era of EVM performance.
            </h2>

            <div className="monad-lab-stat-grid">
              <div className="monad-lab-stat-item stat-item-text">
                <div
                  className="monad-lab-stat-value"
                  data-target="100000"
                  data-suffix=""
                  data-decimals="0"
                >
                  0
                </div>
                <div className="monad-lab-stat-label">TPS</div>
              </div>

              <div className="monad-lab-stat-item stat-item-text">
                <div
                  className="monad-lab-stat-value"
                  data-target="100"
                  data-suffix="%"
                  data-decimals="0"
                >
                  0%
                </div>
                <div className="monad-lab-stat-label">EVM</div>
              </div>

              <div className="monad-lab-stat-item stat-item-text">
                <div
                  className="monad-lab-stat-value"
                  data-target="0.8"
                  data-suffix="s"
                  data-decimals="1"
                >
                  0.0s
                </div>
                <div className="monad-lab-stat-label">Finality</div>
              </div>

              <div className="monad-lab-stat-item stat-item-text">
                <div
                  className="monad-lab-stat-value"
                  data-target="0.4"
                  data-suffix="s"
                  data-decimals="1"
                >
                  0.0s
                </div>
                <div className="monad-lab-stat-label">Block Times</div>

                <div className="monad-lab-radar" ref={radarRef}>
                  <div className="monad-lab-radar-glow radar-ring-static purple-glow" />
                  <div className="monad-lab-radar-ring monad-lab-radar-ring--outer radar-ring-static" />
                  <div className="monad-lab-radar-ring monad-lab-radar-ring--middle radar-ring-static" />
                  <div className="monad-lab-radar-ring monad-lab-radar-ring--inner radar-ring-static" />
                  <div className="monad-lab-radar-center radar-center" />
                </div>
              </div>
            </div>
          </div>

          <div className="monad-lab-chart">
            {bars.map((height, index) => {
              const isHero = index === HERO_BAR_INDEX;
              return (
                <div
                  key={index}
                  className="monad-lab-chart-bar-wrapper"
                >
                  <div
                    className={`monad-lab-chart-bar ${
                      isHero ? "hero-bar" : "chart-bar"
                    }`}
                    style={{ height: `${height}%` }}
                    ref={isHero ? heroBarRef : null}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AtomEVM;
